import * as React from 'react';
import clsx from 'clsx';
import { InputAdornment, MenuItem, Select } from '@mui/material';
import type { SelectProps } from '@mui/material';
import { InputSelect } from 'components/Form/InputSelect';
import { IOption } from 'common/ui/interfaces/selectors';
import { StatusTooltip } from '../Tooltip';
import { StatusTooltipTemplate } from '../Tooltip/template';
import { VerificationResultByRecognition } from 'common/ui/constants/verification-result-by-recognition';
import { IVerificationResultByRecognition } from 'common/ui/interfaces/verification-result-by-recognition';

import { ReactComponent as CheckmarkIcon } from 'assets/images/input-checkmark.svg';
import { ReactComponent as WarningIcon } from 'assets/images/warning.svg';

import styles from './styles.module.scss';

type InputProps = SelectProps & {
  verificationResult?: IVerificationResultByRecognition;
  options: IOption[];
  verified?: boolean;
  hideVerification?: boolean;
  required?: boolean;
};

export const SelectWithStatus = ({
  id,
  label,
  verified,
  value,
  verificationResult = VerificationResultByRecognition,
  onChange,
  error,
  options,
  hideVerification = false,
  onMouseEnter,
  onMouseLeave,
  required = false,
}: InputProps) => {
  return (
    <Select
      required={required}
      value={value}
      onChange={onChange}
      labelId={id}
      id={id}
      inputProps={{
        required,
      }}
      IconComponent={verified ? CheckmarkIcon : WarningIcon}
      input={
        <InputSelect
          id={id}
          label={label}
          error={error}
          required={required}
          endAdornment={
            hideVerification ? undefined : (
              <InputAdornment
                position="end"
                classes={{
                  outlined: styles.adornedEnd,
                }}
              >
                {verified ? (
                  <CheckmarkIcon />
                ) : (
                  <StatusTooltip
                    title={
                      <StatusTooltipTemplate
                        title={verificationResult.title}
                        text={verificationResult.text}
                      />
                    }
                  >
                    <WarningIcon />
                  </StatusTooltip>
                )}
              </InputAdornment>
            )
          }
        />
      }
      renderValue={(v) => options.find((o) => o.value === v)?.label || '-'}
      classes={{
        select: clsx(styles.select),
        icon: styles.icon,
        iconOpen: styles.iconOpen,
      }}
      className={clsx(
        !verified && styles.unverified,
        verified && styles.verified
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {options.map((o) => (
        <MenuItem
          key={o.value}
          value={o.value}
          classes={{ selected: styles.selected }}
        >
          {o.label}
        </MenuItem>
      ))}
    </Select>
  );
};
